
import React, { useState, useEffect } from "react";
import { FormGroup } from "reactstrap";
import "../../styles/Filter.css";
import { useDispatch, useSelector } from "react-redux";
import { getStates, getDistricts, getLevelThree } from "../../services/FilterAPI.tsx";
import StateFilter from "./StateFilter.tsx";
import {
  clearDistrictArray,
  clearLevelThreeArray,
  updateActiveView,
} from "../../redux/slices/filterSectionSlice.ts";
import {
  ACTIVE_VIEW_COUNTRY,
  ACTIVE_VIEW_STATE,
  ALL_PRODUCT,
  ALL_CROPS,
  COUNTRY,
  DATE_FORMAT,
  ACTIVE_VIEW_CONSOLIDATED,
} from "../../utils/Constants.tsx";
import Up from "../../assets/images/arrow_up.svg";
import Down from "../../assets/images/arrow_down.svg";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { getDataWithKeyValue, getSeasonDateRange } from "../../utils/Util.tsx";

export interface FilterFormType {
  state: string;
  district: string;
  distance: number;
  countryView: string;
  gridNumber: string;
  styleView: string;
  startDate: string;
  endDate: string;
  stateStartDate: string | null;
  stateEndDate: string | null;
  levelOne: string;
  levelTwo: string;
  levelThree: string;
  product: string;
  // adjust to array type if needed
  season: string;
  year: string;
  mapView: string;
  claimsCategory: string | null;
}
export const defaultFilter: FilterFormType = {
  state: COUNTRY,
  district: "",
  distance: 0,
  countryView: "",
  gridNumber: "",
  styleView: "",
  startDate: moment().subtract(1, "days").format(DATE_FORMAT.DD_MM_YYYY),
  endDate: moment().format(DATE_FORMAT.DD_MM_YYYY),
  stateStartDate: moment().subtract(1, "days").format(DATE_FORMAT.DD_MM_YYYY),
  stateEndDate: moment().format(DATE_FORMAT.DD_MM_YYYY),
  levelOne: "",
  levelTwo: "",
  levelThree: "",
  product: ALL_PRODUCT,
  season: "",
  year: "",
  mapView: "",
  claimsCategory: null,
};

interface SelectedRow {
  state?: string;
  district?: string;
}
interface FilterSectionProps {
  onApplyFilters: (data: any) => void;
  onClearGridId: () => void;
  gridId: string;
  onDistanceRangeChange: (value: number) => void;
  onStateChange: (value: string) => void;
  selectedRow?: SelectedRow;
}

const districtPH = "Select District";
const level3PH = "Select Level 3";

const FilterSection: React.FC<FilterSectionProps> = ({
  onApplyFilters,
  onClearGridId,
  gridId,
  onDistanceRangeChange,
  onStateChange,
  selectedRow,
}) => {
  //Avoid DOM FormData type
  const [formData, setFormData] = useState<typeof defaultFilter>(defaultFilter);

  // Category kept outside formData
  const [claimsCategory, setClaimsCategory] = useState<string | null>(null);

  const [error, setError] = useState<string>("");
  const [districtList, setDistricts] = useState<string[]>([]);
  const [statePlaceholder, setStatePlaceholder] = useState<string>(COUNTRY);
  const [districtPlaceholder, setDistrictPlaceholder] = useState<string>(districtPH);
  const [level3Placeholder, setLevel3Placeholder] = useState<string>(level3PH);
  const dispatch: any = useDispatch();

  const stateList = [
    COUNTRY,
    ...useSelector((state: any) => state?.filter?.statesData),
  ];
  const seasons = useSelector((state: any) => state?.filter?.seasons);
  const statesWithId = useSelector((state: any) => state?.filter?.statesDataWithId);
  const activeView = useSelector((state: any) => state?.filter?.activeView);
  const districtsData = useSelector((state: any) => state?.filter?.districtsData);

  const [isExpanded, setExpanded] = useState(true);
  const [minDate, setMinDate] = useState<Date | null>(null);
  const [maxDate, setMaxDate] = useState<Date>(moment().toDate());

  const navigate = useNavigate();

  useEffect(() => {
    onStateChange(COUNTRY);
  }, [onStateChange]);

  useEffect(() => {
    dispatch(getStates());
  }, [dispatch]);

  useEffect(() => {
    if (!selectedRow) return;
    setFormData((prev) => ({
      ...prev,
      state: selectedRow.state ?? prev.state,
      district: selectedRow.district ?? prev.district,
    }));
    if (selectedRow.state) setStatePlaceholder(selectedRow.state);
    if (selectedRow.district) setDistrictPlaceholder(selectedRow.district);
  }, [selectedRow]);

  useEffect(() => {
    setDistricts(districtsData || []);
  }, [districtsData]);

  const handleStateChangeLocal = (state: string) => {
    setFormData((prev) => ({
      ...prev,
      state,
      district: "",
      gridNumber: "",
      distance: 0,
    }));
    onStateChange(state);
    setStatePlaceholder(state);
    setDistrictPlaceholder(districtPH);
    setLevel3Placeholder(level3PH);
    setDistricts([]);

    if (state) {
      if (state !== COUNTRY) {
        dispatch(
          getDistricts(getDataWithKeyValue(statesWithId, "vstate", state)?.sid)
        );
      }
      dispatch(
        getLevelThree(getDataWithKeyValue(statesWithId, "vstate", state)?.sid)
      );
    } else {
      dispatch(clearDistrictArray([]));
      dispatch(clearLevelThreeArray([]));
    }
    // reset levelThree
    handleChange({ target: { name: "levelThree", value: "" } });
  };

  const handleDistrictChangeLocal = (district: string) => {
    setFormData((prev) => ({ ...prev, district, gridNumber: "", distance: 0 }));
    setDistrictPlaceholder(district || districtPH);
  };

  const clearFilters = () => {
    setFormData(defaultFilter);
    setStatePlaceholder("Select State");
    setDistrictPlaceholder(districtPH);
    setLevel3Placeholder(level3PH);
    setDistricts([]);
    setClaimsCategory(null);
    setError("");
    onApplyFilters({ ...defaultFilter, claimsCategory: null });
    dispatch(clearDistrictArray([]));
    dispatch(clearLevelThreeArray([]));
  };

  const handleDateChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const onSeasonAndYearChange = () => {
    if (formData?.season?.length > 1) {
      setFormData((prev) => ({
        ...prev,
        stateStartDate: null,
        stateEndDate: null,
      }));
      return;
    }
    const seasonDateRange = getSeasonDateRange(formData, seasons);
    if (seasonDateRange) {
      setMinDate(seasonDateRange?.minDate);
      setMaxDate(seasonDateRange?.maxDate);
      setFormData((prev) => ({
        ...prev,
        stateStartDate: seasonDateRange?.stateStartDate,
        stateEndDate: seasonDateRange?.stateEndDate,
      }));
    }
  };

  useEffect(() => {
    onSeasonAndYearChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData?.season, formData?.year]);

  const handleChange = (e: any) => {
    const { name, value } = e.target;

    if (name === "levelThree") {
      setFormData((prev) => ({ ...prev, levelThree: value }));
      setLevel3Placeholder(value);

    } else if (name === "distance") {
      setFormData((prev) => ({ ...prev, [name]: Number(value) }));
      onDistanceRangeChange(Number(value || 0));

    } else if (name === "levelTwo" && value === "Target") {
      setFormData((prev) => ({
        ...prev,
        levelTwo: value,
        levelThree: ALL_CROPS,
        distance: 0,
      }));
      setLevel3Placeholder(ALL_CROPS);
      setClaimsCategory(null);

    } else if (name === "levelTwo") {
      setFormData((prev) => ({
        ...prev,
        levelTwo: value,
        distance: 0,
      }));
      setClaimsCategory(value === "Claim Processed" ? claimsCategory : null);

    } else if (name === "claimsCategory") {
      setClaimsCategory(value || null);

    } else {
      setFormData((prev) => ({ ...prev, [name]: value, distance: 0 }));
    }
  };

  const clearGridId = () => {
    onClearGridId();
    setFormData((prevState) => ({
      ...prevState,
      gridNumber: "",
      distance: 0,
    }));
    onDistanceRangeChange(0);
  };

  const handleSubmit = () => {
    const isConsolidated =
      formData.levelOne === "Consolidated Business" ||
      formData.levelOne === "Consolidated Manpower";


    if (
      formData.levelOne === "Claims" &&
      formData.levelTwo === "Claim Processed" &&
      !claimsCategory
    )

    if (isConsolidated) {
      if (formData?.season?.length > 0 && !formData?.year) {
        setError("*Please select an year");
        return;
      }
      setError("");
      dispatch(updateActiveView(ACTIVE_VIEW_CONSOLIDATED));
    } else {
      if (formData.state === "") {
        setError("*Please select a state");
        return;
      }
      if (formData.product === "") {
        setError("*Please select a product");
        return;
      }
      if (formData?.season?.length > 0 && !formData?.year) {
        setError("*Please select an year");
        return;
      }
      if (formData.levelOne === "") {
        setError("*Please select Level 1");
        return;
      }
      if (formData.levelTwo === "") {
        setError("*Please select Level 2");
        return;
      }
      if (formData.levelThree === "") {
        setError("*Please select Level 3");
        return;
      }
    }

    setError("");

    if (formData?.state) {
      dispatch(
        updateActiveView(
          isConsolidated
            ? ACTIVE_VIEW_CONSOLIDATED
            : formData?.state === COUNTRY
              ? ACTIVE_VIEW_COUNTRY
              : ACTIVE_VIEW_STATE
        )
      );
    }

    const payload = { ...formData, claimsCategory };
    onApplyFilters(payload);
  };

  return (
    <div className="filter-section-container">
      <div className="d-flex justify-content-end">
        <img
          alt="filter"
          className="expand-filter"
          src={isExpanded ? Up : Down}
          onClick={() => setExpanded(!isExpanded)}
        />
      </div>

      {isExpanded && (
        <>
        {/*ADDED: top-right note */}
          <div className="top-right-note">
            Note: All dates are based on the policy issue date
          </div>
          <div className="filter-section-wrapper">
            


            <div>
              <StateFilter
                formData={formData}
                stateList={stateList}
                districtList={districtList}
                gridId={gridId}
                handleStateChange={handleStateChangeLocal}
                handleDistrictChange={handleDistrictChangeLocal}
                handleDateChange={handleDateChange}
                handleChange={handleChange}
                setFormData={setFormData}
                statePlaceholder={statePlaceholder}
                districtPlaceholder={districtPlaceholder}
                level3Placeholder={level3Placeholder}
                clearFilters={clearFilters}
                handleSubmit={handleSubmit}
                clearGridId={clearGridId}
                minDate={minDate}
                maxDate={maxDate}
                claimsCategory={claimsCategory}
                setClaimsCategory={setClaimsCategory}
              />

              {error && <FormGroup className="error-text">{error}</FormGroup>}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default FilterSection;
